// src/utils/measureHighlighter.js - Simple measure click highlighting with proper coordinate conversion
import { LoopUtils } from './loopUtils.js';

export class MeasureHighlighter {
  constructor(osmd, container, onSelectionChange = null, onMeasureJump = null, isMousePlayMode = null, onPlayNote = null, onExitMousePlayMode = null) {
    this.osmd = osmd;
    this.container = container;
    this.highlightedMeasures = new Set();
    
    // New: Range selection state
    this.selectionMode = 'range'; // 'single' or 'range' - default to range
    this.selectedRange = { start: null, end: null };
    this.tapsDisabled = false; // when set, taps neither select nor jump
    this.rangeHighlights = new Set();
    
    // Track external part visibility state (since OSMD resets instrument.Visible)
    this.partVisibilityState = new Map(); // instrumentId -> boolean
    
    // Callback for immediate state updates
    this.onSelectionChange = onSelectionChange;
    
    // New: Callback for measure jumping during playback
    this.onMeasureJump = onMeasureJump;
    
    // MousePlay mode callbacks
    this.isMousePlayMode = isMousePlayMode;
    this.onPlayNote = onPlayNote;
    this.onExitMousePlayMode = onExitMousePlayMode;
    
    // Track if we're currently playing (set by parent component)
    this.isPlaying = false;
    
    // Arrow key navigation state
    this.currentNavigationMeasure = null;
    this.navigationHighlight = null;
    
    this.setupClickListener();
    this.setupKeyboardListener();
    
  }

  setupClickListener() {
    if (!this.container) return;
    
    // Kept so destroy() can remove it: the score container outlives the
    // highlighter (a new one is created per song), and a leaked listener
    // keeps an old instance's selection state alive and reacting to taps.
    this.clickHandler = (event) => {
      this.handleContainerClick(event);
    };
    this.container.addEventListener('click', this.clickHandler);
  }

  setupKeyboardListener() {
    // Add ESC key listener to clear selection (only when not playing)
    // Add arrow key navigation
    this.keyboardHandler = (event) => {
      if (event.key === 'Escape') {
        // Check if we're in MousePlay mode first
        if (this.isMousePlayMode && typeof this.isMousePlayMode === 'function' && this.isMousePlayMode()) {
          // Exit MousePlay mode by calling the parent component's toggle function
          if (this.onExitMousePlayMode && typeof this.onExitMousePlayMode === 'function') {
            this.onExitMousePlayMode();
          }
          event.preventDefault();
        } else if ((this.selectedRange.start !== null) && !this.isPlaying) {
          // Normal ESC behavior - clear selection when not playing
          this.clearAllHighlights();
          event.preventDefault();
        }
      }
      // Arrow key navigation for measures
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        // Don't interfere with text input
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
          return;
        }
        this.handleArrowNavigation(event.key === 'ArrowRight');
        event.preventDefault();
      }
    };
    
    document.addEventListener('keydown', this.keyboardHandler);
  }

  // Cleanup method for removing event listeners
  destroy() {
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
      this.keyboardHandler = null;
    }
    if (this.clickHandler && this.container) {
      this.container.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }
    this.clearNavigationHighlight();
    this.onSelectionChange = null;
    this.onMeasureJump = null;
  }

  handleContainerClick(event) {
    if (!this.osmd?.graphic) {
      return;
    }

    // Get the SVG element
    const svg = this.container.querySelector('svg');
    if (!svg) {
      return;
    }

    // Get click coordinates relative to the SVG element
    const svgRect = svg.getBoundingClientRect();
    const clientX = event.clientX - svgRect.left;
    const clientY = event.clientY - svgRect.top;
    
    // Convert client coordinates to SVG coordinate space using the viewBox
    const viewBox = svg.viewBox.baseVal;
    let svgX, svgY;
    
    if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
      // Account for viewBox transformation
      svgX = (clientX / svgRect.width) * viewBox.width + viewBox.x;
      svgY = (clientY / svgRect.height) * viewBox.height + viewBox.y;
    } else {
      // No viewBox, use client coordinates directly
      svgX = clientX;
      svgY = clientY;
    }
    
    // Convert to OSMD units (OSMD units are typically pixels/10)
    const osmdX = svgX / 10;
    const osmdY = svgY / 10;
    
    // Check if we're in MousePlay mode
    const isInMousePlayMode = this.isMousePlayMode && typeof this.isMousePlayMode === 'function' && this.isMousePlayMode();
    
    if (isInMousePlayMode) {
      // In MousePlay mode, find and play the clicked note
      this.findAndPlayNoteAtCoordinates(osmdX, osmdY, svgX, svgY);
      return; // Don't continue to measure selection
    } else {
      // Normal mode: find measure for selection
      this.findMeasureAtCoordinates(osmdX, osmdY, svgX, svgY);
    }
  }

  findMeasureAtCoordinates(osmdX, osmdY, svgX, svgY) {
    if (!this.osmd?.graphic?.measureList) {
      return;
    }

    let closestMeasure = null;
    let closestDistance = Infinity;
    let visibleMeasuresChecked = 0;
    let totalMeasuresChecked = 0;

    // First try using actual SVG bounding boxes for more accurate hit testing
    for (let measureIndex = 0; measureIndex < this.osmd.graphic.measureList.length; measureIndex++) {
      const measureRow = this.osmd.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const graphicalMeasure = measureRow[staffIndex];
        totalMeasuresChecked++;
        
        if (graphicalMeasure && graphicalMeasure.PositionAndShape) {
          const isVisible = this.isMeasureVisible(graphicalMeasure);
          
          if (isVisible) {
            visibleMeasuresChecked++;
            
            // Use the same bounds calculation as navigation highlight (which works correctly)
            let bounds = null;
            try {
              const boundingBox = this.getMeasureBoundingBox(graphicalMeasure);
              bounds = {
                left: boundingBox.x,
                top: boundingBox.y,
                right: boundingBox.x + boundingBox.width,
                bottom: boundingBox.y + boundingBox.height
              };
            } catch (error) {
              // Fallback to simple OSMD coordinates if getMeasureBoundingBox fails
              const pos = graphicalMeasure.PositionAndShape.AbsolutePosition;
              const measureLeft = pos.x * 10;
              const measureTop = pos.y * 10;
              const measureRight = (graphicalMeasure.PositionAndShape.BorderRight || (pos.x + 8)) * 10;
              const measureBottom = measureTop + 60;
              
              bounds = {
                left: measureLeft,
                top: measureTop,
                right: measureRight,
                bottom: measureBottom
              };
            }
            
            
            // Check if click is within measure bounds (use SVG coordinates for consistency)
            const tolerance = 15; // pixels (more generous for better click detection)
            const inBounds = svgX >= (bounds.left - tolerance) && 
                            svgX <= (bounds.right + tolerance) && 
                            svgY >= (bounds.top - tolerance) && 
                            svgY <= (bounds.bottom + tolerance);
            
            if (inBounds) {
              // Calculate distance to center of measure
              const centerX = (bounds.left + bounds.right) / 2;
              const centerY = (bounds.top + bounds.bottom) / 2;
              const distance = Math.sqrt(Math.pow(svgX - centerX, 2) + Math.pow(svgY - centerY, 2));
              
              if (distance < closestDistance) {
                closestDistance = distance;
                closestMeasure = { graphicalMeasure, measureIndex, staffIndex };
              }
            }
          }
        }
      }
    }

    if (closestMeasure) {
      this.toggleMeasureHighlight(closestMeasure.graphicalMeasure);
    } else {
      // Fallback: try with OSMD coordinates and larger tolerance
      const fallbackFound = this.findMeasureWithLargeTolerance(osmdX, osmdY);
      
      // If no measure found even with fallback, clear selection
      if (!fallbackFound) {
        this.clearAllHighlights();
      }
    }
  }

  findMeasureWithLargeTolerance(osmdX, osmdY) {
    if (!this.osmd?.graphic?.measureList) return false;

    let closestMeasure = null;
    let closestDistance = Infinity;

    for (let measureIndex = 0; measureIndex < this.osmd.graphic.measureList.length; measureIndex++) {
      const measureRow = this.osmd.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const graphicalMeasure = measureRow[staffIndex];
        
        if (graphicalMeasure && graphicalMeasure.PositionAndShape) {
          // CRITICAL: Only consider visible measures in fallback too
          const isVisible = this.isMeasureVisible(graphicalMeasure);
          
          if (isVisible) {
            const pos = graphicalMeasure.PositionAndShape.AbsolutePosition;
            const centerX = pos.x + 5; // Approximate center
            const centerY = pos.y + 4; // Approximate center
            
            const distance = Math.sqrt(Math.pow(osmdX - centerX, 2) + Math.pow(osmdY - centerY, 2));
            
            if (distance < closestDistance) {
              closestDistance = distance;
              closestMeasure = { graphicalMeasure, distance };
            }
          }
        }
      }
    }

    if (closestMeasure && closestMeasure.distance < 8) { // More generous tolerance for fallback
      this.toggleMeasureHighlight(closestMeasure.graphicalMeasure);
      return true; // Found a measure
    } else {
      return false; // No measure found
    }
  }

  setTapsDisabled(disabled) {
    this.tapsDisabled = !!disabled;
  }

  toggleMeasureHighlight(graphicalMeasure) {
    if (this.tapsDisabled) return;
    const measureId = this.getMeasureId(graphicalMeasure);
    const measureNumber = graphicalMeasure.MeasureNumber;
    
    // If playing, jump to measure instead of selecting
    if (this.isPlaying && this.onMeasureJump) {
      // console.log(`🦘 Jumping to measure ${measureNumber} during playback`);
      this.onMeasureJump(measureNumber);
      return;
    }
    
    // Normal selection behavior when not playing
    if (this.selectionMode === 'single') {
      // Original behavior - toggle individual measures
      if (this.highlightedMeasures.has(measureId)) {
        this.unhighlightMeasure(measureId);
      } else {
        this.highlightMeasure(measureId, graphicalMeasure);
      }
    } else if (this.selectionMode === 'range') {
      this.handleRangeSelection(measureNumber, graphicalMeasure);
    }
  }

  // New method for range selection logic
  handleRangeSelection(measureNumber, graphicalMeasure) {
    
    if (this.selectedRange.start === null) {
      // First click - set start
      this.selectedRange.start = measureNumber;
      this.selectedRange.end = null;
      this.createSimpleHighlight(measureNumber, measureNumber, 'blue', '0.3');
      this.notifySelectionChange();
    } else if (this.selectedRange.end === null) {
      // Second click - set end and create range
      this.selectedRange.end = measureNumber;
      
      // Ensure start <= end
      if (this.selectedRange.start > this.selectedRange.end) {
        [this.selectedRange.start, this.selectedRange.end] = [this.selectedRange.end, this.selectedRange.start];
      }
      
      
      // Clear old and create new range
      this.clearRangeHighlights();
      this.createSimpleHighlight(this.selectedRange.start, this.selectedRange.end, 'lightblue', '0.3');
      this.notifySelectionChange();
    } else {
      // Third click - reset
      this.clearRangeHighlights();
      this.selectedRange.start = measureNumber;
      this.selectedRange.end = null;
      this.createSimpleHighlight(measureNumber, measureNumber, 'blue', '0.3');
      this.notifySelectionChange();
    }
  }

  createSimpleHighlight(startMeasure, endMeasure, color, opacity) {
    const svg = this.container.querySelector('svg');
    if (!svg) {
      return;
    }

    // Clear any existing highlights for this range first
    this.clearRangeHighlights();

    // Collect ONLY visible measures in the range, grouped by system
    const systemMeasures = new Map(); // systemIndex -> [unique measures by number]
    
    for (let systemIndex = 0; systemIndex < this.osmd.graphic.measureList.length; systemIndex++) {
      const measureRow = this.osmd.graphic.measureList[systemIndex];
      const seenMeasureNumbers = new Set();
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && 
            measure.MeasureNumber >= startMeasure && 
            measure.MeasureNumber <= endMeasure) {
          
          // CRITICAL: Only process measures that are actually visible in the DOM
          const isVisible = this.isMeasureVisible(measure);
          if (isVisible && !seenMeasureNumbers.has(measure.MeasureNumber)) {
            seenMeasureNumbers.add(measure.MeasureNumber);
            
            if (!systemMeasures.has(systemIndex)) {
              systemMeasures.set(systemIndex, []);
            }
            systemMeasures.get(systemIndex).push(measure);
          }
        }
      }
    }

    // Create ONE rectangle per system that spans all visible measures in that system
    systemMeasures.forEach((measures, systemIndex) => {
      if (measures.length === 0) return;
      
      // Sort measures by number for proper bounds calculation
      measures.sort((a, b) => a.MeasureNumber - b.MeasureNumber);
      
      // Calculate combined bounds using actual SVG coordinates where possible
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      let hasValidBounds = false;
      
      measures.forEach((measure, index) => {
        try {
          const bounds = this.getMeasureBoundingBox(measure);
          
          // Validate bounds before using them
          if (isFinite(bounds.x) && isFinite(bounds.y) && 
              isFinite(bounds.width) && isFinite(bounds.height) &&
              bounds.width > 0 && bounds.height > 0) {
            
            // For continuous highlighting, use the full width of first and last measures
            // but only the left edge of intermediate measures to avoid gaps
            let leftEdge = bounds.x;
            let rightEdge = bounds.x + bounds.width;
            
            // Special handling for edge cases to ensure continuous highlighting
            if (measures.length === 1) {
              // Single measure - use full bounds
              minX = Math.min(minX, leftEdge);
              maxX = Math.max(maxX, rightEdge);
            } else if (index === 0) {
              // First measure - use left edge and set initial right boundary
              minX = leftEdge;
              maxX = rightEdge;
            } else if (index === measures.length - 1) {
              // Last measure - extend to its right edge
              maxX = rightEdge;
              minX = Math.min(minX, leftEdge);
            } else {
              // Intermediate measures - just ensure continuity
              minX = Math.min(minX, leftEdge);
              maxX = Math.max(maxX, rightEdge);
            }
            
            // For Y coordinates, encompass all staves
            minY = Math.min(minY, bounds.y);
            maxY = Math.max(maxY, bounds.y + bounds.height);
            hasValidBounds = true;
          }
        } catch (error) {
        }
      });
      
      if (!hasValidBounds) {
        return;
      }
      
      // Create the highlight rectangle
      const id = `simple_range_${systemIndex}_${startMeasure}_${endMeasure}`;
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      
      // Add small padding to ensure good visual coverage
      const padding = 3;
      rect.setAttribute('x', minX - padding);
      rect.setAttribute('y', minY - padding);
      rect.setAttribute('width', maxX - minX + (2 * padding));
      rect.setAttribute('height', maxY - minY + (2 * padding));
      rect.setAttribute('fill', color);
      rect.setAttribute('fill-opacity', opacity);
      rect.setAttribute('stroke', 'none');
      rect.setAttribute('data-range-highlight', id);
      rect.setAttribute('pointer-events', 'none');
      
      // Insert at the beginning so it appears behind the score
      svg.insertBefore(rect, svg.firstChild);
      this.rangeHighlights.add(id);
    });
  }

  createSimpleHighlightNew(startMeasure, endMeasure, color, opacity) {
    
    const svg = this.container.querySelector('svg');
    if (!svg) {
      return;
    }

    // Find which systems contain our range and create ONE continuous rectangle per system
    for (let systemIndex = 0; systemIndex < this.osmd.graphic.measureList.length; systemIndex++) {
      const measureRow = this.osmd.graphic.measureList[systemIndex];
      
      // Find the leftmost and rightmost selected measures in this system
      let leftmostMeasure = null;
      let rightmostMeasure = null;
      let systemHasSelectedMeasures = false;
      const seenMeasureNumbers = new Set();
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && 
            measure.MeasureNumber >= startMeasure && 
            measure.MeasureNumber <= endMeasure &&
            this.isMeasureVisible(measure) &&
            !seenMeasureNumbers.has(measure.MeasureNumber)) {
          
          seenMeasureNumbers.add(measure.MeasureNumber);
          systemHasSelectedMeasures = true;
          
          // Find leftmost measure in this system
          if (!leftmostMeasure || measure.MeasureNumber < leftmostMeasure.MeasureNumber) {
            leftmostMeasure = measure;
          }
          
          // Find rightmost measure in this system  
          if (!rightmostMeasure || measure.MeasureNumber > rightmostMeasure.MeasureNumber) {
            rightmostMeasure = measure;
          }
        }
      }
      
      // If this system has selected measures, create one rectangle spanning from leftmost to rightmost
      if (systemHasSelectedMeasures && leftmostMeasure && rightmostMeasure) {
        
        const leftBounds = this.getMeasureBoundingBox(leftmostMeasure);
        const rightBounds = this.getMeasureBoundingBox(rightmostMeasure);
        
        // Calculate system height by checking all staves in this system
        let systemMinY = Infinity, systemMaxY = -Infinity;
        for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
          const measure = measureRow[staffIndex];
          if (measure && this.isMeasureVisible(measure)) {
            const bounds = this.getMeasureBoundingBox(measure);
            systemMinY = Math.min(systemMinY, bounds.y);
            systemMaxY = Math.max(systemMaxY, bounds.y + bounds.height);
          }
        }
        
        // Create one continuous rectangle from leftmost to rightmost measure
        // Use ONLY the absolute positions - no adding widths that might overlap
        const startX = leftBounds.x;
        const endX = rightBounds.x + (rightBounds.width * 0.9); // Use 90% of last measure width to avoid barline
        const width = endX - startX;
        
        
        const id = `system_rect_${systemIndex}`;
        
        // Remove existing rectangle for this system if it exists
        const existing = svg.querySelector(`rect[data-range-highlight="${id}"]`);
        if (existing) existing.remove();
        
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', startX - 3);
        rect.setAttribute('y', systemMinY - 2);
        rect.setAttribute('width', width + 6);
        rect.setAttribute('height', systemMaxY - systemMinY + 4);
        rect.setAttribute('fill', color);
        rect.setAttribute('fill-opacity', opacity);
        rect.setAttribute('stroke', 'none');
        rect.setAttribute('data-range-highlight', id);
        rect.setAttribute('pointer-events', 'none');
        
        svg.insertBefore(rect, svg.firstChild);
        this.rangeHighlights.add(id);
        
      }
    }
  }

  getMeasureId(graphicalMeasure) {
    return `measure_${graphicalMeasure.MeasureNumber}_${graphicalMeasure.ParentStaff?.Id || 0}`;
  }

 highlightMeasure(measureId, graphicalMeasure) {
  const svg = this.container.querySelector('svg');
  if (!svg) return;

  try {
    // Get the proper measure bounds across all staves
    const measureBounds = this.getMeasureBoundingBox(graphicalMeasure);
    

    const highlight = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    highlight.setAttribute('x', measureBounds.x - 3);
    highlight.setAttribute('y', measureBounds.y - 10);
    highlight.setAttribute('width', measureBounds.width + 6);
    highlight.setAttribute('height', measureBounds.height + 20);
    highlight.setAttribute('fill', 'yellow');
    highlight.setAttribute('fill-opacity', '0.2');
    highlight.setAttribute('stroke', 'orange');
    highlight.setAttribute('stroke-width', '2');
    highlight.setAttribute('data-measure-highlight', measureId);
    
    svg.appendChild(highlight);
    this.highlightedMeasures.add(measureId);
  } catch (error) {
  }
}

getMeasureBoundingBox(clickedMeasure) {
  const measureNumber = clickedMeasure.MeasureNumber;
  
  // Find all VISIBLE measures with the same measure number (across all visible staves)
  const allMeasuresInThisColumn = [];
  
  if (this.osmd?.graphic?.measureList) {
    for (let measureIndex = 0; measureIndex < this.osmd.graphic.measureList.length; measureIndex++) {
      const measureRow = this.osmd.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && measure.MeasureNumber === measureNumber) {
          // CRITICAL: Only include measures that are actually visible in the DOM
          const isVisible = this.isMeasureVisible(measure);
          if (isVisible) {
            allMeasuresInThisColumn.push(measure);
          }
        }
      }
    }
  }
  
  if (allMeasuresInThisColumn.length === 0) {
    // Fallback to just the clicked measure's SVG bounds if available
    try {
      const svgElement = clickedMeasure.getSVGGElement ? clickedMeasure.getSVGGElement() : null;
      if (svgElement) {
        const bbox = svgElement.getBBox();
        if (bbox && bbox.width > 0 && bbox.height > 0) {
          return {
            x: bbox.x,
            y: bbox.y - 5,
            width: bbox.width,
            height: bbox.height + 10
          };
        }
      }
    } catch (error) {
      // Continue to fallback below
    }
    
    // Last resort fallback using OSMD coordinates
    const pos = clickedMeasure.PositionAndShape.AbsolutePosition;
    return {
      x: pos.x * 10,
      y: pos.y * 10 - 30,
      width: 100,
      height: 120
    };
  }
  
  // Calculate bounds using ONLY actual rendered SVG elements
  let hasValidBounds = false;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  
  allMeasuresInThisColumn.forEach(measure => {
    try {
      const svgElement = measure.getSVGGElement ? measure.getSVGGElement() : null;
      if (svgElement) {
        // Double-check visibility in the DOM
        const computedStyle = window.getComputedStyle(svgElement);
        const isActuallyVisible = computedStyle.display !== 'none' && 
                                 computedStyle.visibility !== 'hidden' && 
                                 parseFloat(computedStyle.opacity) > 0;
        
        if (isActuallyVisible) {
          const bbox = svgElement.getBBox();
          if (bbox && bbox.width > 0 && bbox.height > 0) {
            // Use actual SVG pixel coordinates directly
            minX = Math.min(minX, bbox.x);
            minY = Math.min(minY, bbox.y);
            maxX = Math.max(maxX, bbox.x + bbox.width);
            maxY = Math.max(maxY, bbox.y + bbox.height);
            hasValidBounds = true;
          }
        }
      }
    } catch (error) {
      // Skip this measure if SVG access fails
    }
  });
  
  // If we couldn't get SVG bounds, fall back to OSMD coordinates
  if (!hasValidBounds) {
    allMeasuresInThisColumn.forEach(measure => {
      if (measure.PositionAndShape) {
        const pos = measure.PositionAndShape.AbsolutePosition;
        const measureLeft = pos.x * 10;
        const staffTop = pos.y * 10;
        
        // Calculate width using OSMD data
        let width = 80; // default
        if (measure.stave && measure.stave.width) {
          width = measure.stave.width;
        } else if (measure.staffEntries && measure.staffEntries.length > 0) {
          const firstEntry = measure.staffEntries[0];
          const lastEntry = measure.staffEntries[measure.staffEntries.length - 1];
          if (firstEntry.PositionAndShape && lastEntry.PositionAndShape) {
            const firstX = firstEntry.PositionAndShape.AbsolutePosition.x * 10;
            const lastX = lastEntry.PositionAndShape.AbsolutePosition.x * 10;
            width = Math.max(60, (lastX - measureLeft) + 40);
          }
        }
        
        minX = Math.min(minX, measureLeft);
        minY = Math.min(minY, staffTop);
        maxX = Math.max(maxX, measureLeft + width);
        maxY = Math.max(maxY, staffTop + 60); // default staff height
        hasValidBounds = true;
      }
    });
  }
  
  if (!hasValidBounds) {
    // Final fallback
    const pos = clickedMeasure.PositionAndShape.AbsolutePosition;
    return {
      x: pos.x * 10,
      y: pos.y * 10 - 30,
      width: 100,
      height: 120
    };
  }
  
  return {
    x: minX,
    y: minY - 5, // Small padding
    width: maxX - minX,
    height: maxY - minY + 10 // Small padding
  };
}

  unhighlightMeasure(measureId) {
    const highlight = this.container.querySelector(`rect[data-measure-highlight="${measureId}"]`);
    if (highlight) {
      highlight.remove();
      this.highlightedMeasures.delete(measureId);
    }
  }

  clearAllHighlights() {
    const highlights = this.container.querySelectorAll('rect[data-measure-highlight]');
    highlights.forEach(h => h.remove());
    this.highlightedMeasures.clear();
    this.clearRangeHighlights();
    
    // Reset range selection state
    this.selectedRange = { start: null, end: null };
    this.notifySelectionChange();
  }

  // New methods for range highlighting
  clearRangeHighlights() {
    const rangeHighlights = this.container.querySelectorAll('rect[data-range-highlight]');
    rangeHighlights.forEach(h => h.remove());
    this.rangeHighlights.clear();
  }

  highlightRangeStart(measureNumber) {
    // For single measure, use the old individual highlighting
    this.highlightSingleMeasureRange(measureNumber, 'blue', '0.15', 'Range Start');
  }

  highlightRange(startMeasure, endMeasure) {
    // For range, use the new system-based continuous highlighting
    this.highlightMeasuresInRange(startMeasure, endMeasure, 'lightblue', '0.08', 'Selected Range');
  }

  highlightSingleMeasureRange(measureNumber, color, opacity, label) {
    // Simple single measure highlighting for range start
    if (!this.osmd?.graphic?.measureList) return;
    
    
    for (let systemIndex = 0; systemIndex < this.osmd.graphic.measureList.length; systemIndex++) {
      const measureRow = this.osmd.graphic.measureList[systemIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && measure.MeasureNumber === measureNumber && this.isMeasureVisible(measure)) {
          this.highlightMeasureForRange(measure, color, opacity, label);
        }
      }
    }
  }

  highlightMeasuresInRange(startMeasure, endMeasure, color, opacity, label) {
    if (!this.osmd?.graphic?.measureList) return;


    // Group measures by system (row) for continuous highlighting
    const systemGroups = new Map(); // systemIndex -> measures in that system
    
    // Find all VISIBLE measures in the range across all visible staves
    for (let systemIndex = 0; systemIndex < this.osmd.graphic.measureList.length; systemIndex++) {
      const measureRow = this.osmd.graphic.measureList[systemIndex];
      
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && 
            measure.MeasureNumber >= startMeasure && 
            measure.MeasureNumber <= endMeasure) {
          
          
          // Only highlight visible measures
          if (this.isMeasureVisible(measure)) {
            if (!systemGroups.has(systemIndex)) {
              systemGroups.set(systemIndex, []);
            }
            systemGroups.get(systemIndex).push(measure);
          } else {
          }
        }
      }
    }

    systemGroups.forEach((measures, systemIndex) => {
      const measureNumbers = measures.map(m => m.MeasureNumber);
    });

    // Create one continuous rectangle per system
    systemGroups.forEach((measures, systemIndex) => {
      this.highlightSystemRange(measures, systemIndex, color, opacity, label);
    });
  }

  highlightSystemRange(measures, systemIndex, color, opacity, label) {
    const svg = this.container.querySelector('svg');
    if (!svg || measures.length === 0) {
      return;
    }

    try {
      
      // Calculate the continuous bounds across all measures in this system
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      measures.forEach((measure, index) => {
        const bounds = this.getMeasureBoundingBox(measure);
        
        minX = Math.min(minX, bounds.x);
        minY = Math.min(minY, bounds.y);
        maxX = Math.max(maxX, bounds.x + bounds.width);
        maxY = Math.max(maxY, bounds.y + bounds.height);
      });
      
      
      const systemId = `system_range_${systemIndex}_${measures[0].MeasureNumber}_${measures[measures.length-1].MeasureNumber}`;
      
      // Remove existing highlight for this system if it exists
      const existing = svg.querySelector(`rect[data-range-highlight="${systemId}"]`);
      if (existing) {
        existing.remove();
      }
      
      const highlight = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      highlight.setAttribute('x', minX - 3);
      highlight.setAttribute('y', minY);
      highlight.setAttribute('width', maxX - minX + 6);
      highlight.setAttribute('height', maxY - minY);
      highlight.setAttribute('fill', color);
      highlight.setAttribute('fill-opacity', opacity);
      highlight.setAttribute('stroke', 'none');
      highlight.setAttribute('data-range-highlight', systemId);
      highlight.setAttribute('pointer-events', 'none');
      
      // Insert at the beginning so it's behind notes
      svg.insertBefore(highlight, svg.firstChild);
      this.rangeHighlights.add(systemId);
      
      const measureNumbers = measures.map(m => m.MeasureNumber).sort((a,b) => a-b);
      
    } catch (error) {
    }
  }

  highlightMeasureForRange(graphicalMeasure, color, opacity, label) {
    const svg = this.container.querySelector('svg');
    if (!svg) return;

    try {
      const measureBounds = this.getMeasureBoundingBox(graphicalMeasure);
      const rangeId = `range_${graphicalMeasure.MeasureNumber}_${graphicalMeasure.ParentStaff?.Id || 0}`;
      
      const highlight = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      highlight.setAttribute('x', measureBounds.x - 3);
      highlight.setAttribute('y', measureBounds.y - 10);
      highlight.setAttribute('width', measureBounds.width + 6);
      highlight.setAttribute('height', measureBounds.height + 20);
      highlight.setAttribute('fill', color);
      highlight.setAttribute('fill-opacity', opacity);
      highlight.setAttribute('stroke', color);
      highlight.setAttribute('stroke-width', '3');
      highlight.setAttribute('data-range-highlight', rangeId);
      
      svg.appendChild(highlight);
      this.rangeHighlights.add(rangeId);
      
    } catch (error) {
    }
  }

  // Public API methods
  setSelectionMode(mode) {
    this.selectionMode = mode;
    
    // Clear current selection when switching modes
    if (mode === 'range') {
      this.clearAllHighlights();
      this.selectedRange = { start: null, end: null };
    } else {
      this.clearRangeHighlights();
    }
  }

  // Set playing state to control click behavior
  setPlayingState(isPlaying) {
    this.isPlaying = isPlaying;
  }

  // Get the start time for a measure (for jumping)
  getMeasureStartTime(measureNumber, allNotesWithTiming) {
    if (!allNotesWithTiming || allNotesWithTiming.length === 0) {
      // console.log('❌ No notes available for measure timing calculation');
      return 0;
    }

    // Build measure timing map
    const measureTimingMap = LoopUtils.buildMeasureTimingMap(this.osmd, allNotesWithTiming);
    // console.log(`📊 Measure timing map has ${measureTimingMap.size} measures`);
    
    // Get timing for the requested measure
    const measureTiming = measureTimingMap.get(measureNumber);
    
    if (measureTiming) {
      // console.log(`📍 Measure ${measureNumber} starts at time: ${measureTiming.startTime}, noteCount: ${measureTiming.noteCount}`);
      
      // Check if this measure has any notes
      const notesInMeasure = allNotesWithTiming.filter(note => 
        note.startTime >= measureTiming.startTime && 
        note.startTime < measureTiming.endTime
      );
      // console.log(`🎵 Found ${notesInMeasure.length} notes in measure ${measureNumber}`);
      
      return measureTiming.startTime;
    }
    
    // Fallback: estimate based on measure position
    const totalMeasures = LoopUtils.countMeasuresInScore(this.osmd);
    if (totalMeasures > 0) {
      const totalDuration = Math.max(...allNotesWithTiming.map(n => n.endTime));
      const estimatedStartTime = ((measureNumber - 1) / totalMeasures) * totalDuration;
      // console.log(`📍 Estimated measure ${measureNumber} starts at time: ${estimatedStartTime} (fallback)`);
      return estimatedStartTime;
    }
    
    // console.log(`❌ Could not determine measure start time for measure ${measureNumber}, defaulting to 0`);
    return 0;
  }

  getSelectedRange() {
    return this.selectedRange;
  }

  // Helper method to notify about selection changes
  notifySelectionChange() {
    if (this.onSelectionChange) {
      this.onSelectionChange({ ...this.selectedRange });
    }
  }

  hasCompleteRange() {
    return this.selectedRange.start !== null && this.selectedRange.end !== null;
  }

  // Update part visibility state (called from parent component)
  updatePartVisibility(partControls) {
    this.partVisibilityState.clear();
    if (partControls && partControls.length > 0) {
      partControls.forEach(part => {
        this.partVisibilityState.set(part.id, part.visible !== false);
      });
    }
  }

  // Check if a measure is currently visible (not hidden due to part visibility)
  isMeasureVisible(measure) {
    try {
      // PRIMARY METHOD: Check external visibility state (most reliable since OSMD resets instrument.Visible)
      if (measure.ParentStaff?.ParentInstrument) {
        const instrumentId = measure.ParentStaff.ParentInstrument.Id;
        if (this.partVisibilityState.has(instrumentId)) {
          const isPartVisible = this.partVisibilityState.get(instrumentId);
          if (!isPartVisible) {
            return false; // Part is explicitly hidden
          }
        }
      }

      // SECONDARY METHOD: Check SVG element visibility (for cases where CSS is used)
      const svgElement = measure.getSVGGElement ? measure.getSVGGElement() : null;
      if (svgElement) {
        const computedStyle = window.getComputedStyle(svgElement);
        const isDisplayed = computedStyle.display !== 'none';
        const isVisible = computedStyle.visibility !== 'hidden';
        const hasOpacity = parseFloat(computedStyle.opacity) > 0;
        const svgVisible = isDisplayed && isVisible && hasOpacity;
        
        // Also check if the element has any actual content (non-zero bounding box)
        try {
          const bbox = svgElement.getBBox();
          const hasContent = bbox && (bbox.width > 0 || bbox.height > 0);
          return svgVisible && hasContent;
        } catch (e) {
          return svgVisible;
        }
      }

      // FALLBACK 1: Check parent instrument visibility (unreliable due to reset after render)
      if (measure.ParentStaff?.ParentInstrument) {
        const instrument = measure.ParentStaff.ParentInstrument;
        if (instrument.Visible !== undefined) {
          return instrument.Visible;
        }
      }

      // FALLBACK 2: Default to visible if we can't determine and no explicit hiding
      return true;
      
    } catch (error) {
      return false; // Default to hidden on error to be safe
    }
  }

  // New method to refresh after layout changes
  refreshAfterLayoutChange() {
    // Clear any cached position data or stale highlights
    // This should be called after OSMD re-renders due to part visibility changes
    
    // Clear all existing highlights immediately to prevent stale rectangles
    this.clearRangeHighlights();
    
    // If we have a current selection, we need to refresh the highlights
    if (this.selectedRange.start !== null) {
      // Use a longer delay to ensure OSMD layout has fully stabilized
      // This is critical because OSMD's SVG rendering may take some time
      setTimeout(() => {
        try {
          // Verify that we can still find visible measures for the selection
          const hasVisibleMeasuresInRange = this.hasVisibleMeasuresInRange(
            this.selectedRange.start, 
            this.selectedRange.end || this.selectedRange.start
          );
          
          if (hasVisibleMeasuresInRange) {
            // Re-create highlights with fresh layout data
            if (this.selectedRange.end !== null) {
              // Complete range
              this.createSimpleHighlight(this.selectedRange.start, this.selectedRange.end, 'lightblue', '0.3');
            } else {
              // Single measure
              this.createSimpleHighlight(this.selectedRange.start, this.selectedRange.start, 'blue', '0.3');
            }
          } else {
            this.selectedRange = { start: null, end: null };
          }
        } catch (error) {
          // Reset selection on error to prevent broken state
          this.selectedRange = { start: null, end: null };
        }
      }, 150); // Increased delay to ensure SVG rendering is complete
    }
  }
  
  // Helper method to check if there are visible measures in a range
  hasVisibleMeasuresInRange(startMeasure, endMeasure) {
    if (!this.osmd?.graphic?.measureList) return false;
    
    for (let systemIndex = 0; systemIndex < this.osmd.graphic.measureList.length; systemIndex++) {
      const measureRow = this.osmd.graphic.measureList[systemIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && 
            measure.MeasureNumber >= startMeasure && 
            measure.MeasureNumber <= endMeasure &&
            this.isMeasureVisible(measure)) {
          return true;
        }
      }
    }
    
    return false;
  }

  // ARROW KEY NAVIGATION METHODS
  handleArrowNavigation(isRight) {
    if (!this.osmd?.graphic?.measureList) return;
    
    // Get all visible measure numbers
    const visibleMeasures = this.getVisibleMeasureNumbers();
    if (visibleMeasures.length === 0) return;
    
    visibleMeasures.sort((a, b) => a - b); // Ensure sorted order
    
    let targetMeasure;
    
    if (this.currentNavigationMeasure === null) {
      // Start with first measure
      targetMeasure = visibleMeasures[0];
    } else {
      const currentIndex = visibleMeasures.indexOf(this.currentNavigationMeasure);
      
      if (isRight) {
        // Move to next measure (wrap to first if at end)
        targetMeasure = currentIndex < visibleMeasures.length - 1 
          ? visibleMeasures[currentIndex + 1] 
          : visibleMeasures[0];
      } else {
        // Move to previous measure (wrap to last if at beginning)
        targetMeasure = currentIndex > 0 
          ? visibleMeasures[currentIndex - 1] 
          : visibleMeasures[visibleMeasures.length - 1];
      }
    }
    
    this.navigateToMeasure(targetMeasure);
  }
  
  getVisibleMeasureNumbers() {
    const measureNumbers = new Set();
    
    for (let measureIndex = 0; measureIndex < this.osmd.graphic.measureList.length; measureIndex++) {
      const measureRow = this.osmd.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const graphicalMeasure = measureRow[staffIndex];
        
        if (graphicalMeasure && this.isMeasureVisible(graphicalMeasure)) {
          measureNumbers.add(graphicalMeasure.MeasureNumber);
        }
      }
    }
    
    return Array.from(measureNumbers);
  }
  
  navigateToMeasure(measureNumber) {
    this.clearNavigationHighlight();
    this.currentNavigationMeasure = measureNumber;
    this.createNavigationHighlight(measureNumber);
    
    console.log(`📍 Navigated to measure ${measureNumber} (use ← → to navigate)`);
    
    // If playing, also jump to this measure
    if (this.isPlaying && this.onMeasureJump) {
      this.onMeasureJump(measureNumber);
    }
  }
  
  createNavigationHighlight(measureNumber) {
    const svg = this.container.querySelector('svg');
    if (!svg) return;
    
    // Find all visible measures with this number
    for (let measureIndex = 0; measureIndex < this.osmd.graphic.measureList.length; measureIndex++) {
      const measureRow = this.osmd.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && measure.MeasureNumber === measureNumber && this.isMeasureVisible(measure)) {
          try {
            const bounds = this.getMeasureBoundingBox(measure);
            
            const highlight = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            highlight.setAttribute('x', bounds.x - 5);
            highlight.setAttribute('y', bounds.y - 5);
            highlight.setAttribute('width', bounds.width + 10);
            highlight.setAttribute('height', bounds.height + 10);
            highlight.setAttribute('fill', 'none');
            highlight.setAttribute('stroke', 'orange');
            highlight.setAttribute('stroke-width', '3');
            highlight.setAttribute('stroke-dasharray', '10,5');
            highlight.setAttribute('data-navigation-highlight', 'true');
            highlight.setAttribute('pointer-events', 'none');
            
            svg.appendChild(highlight);
          } catch (error) {
            console.log(`❌ Failed to create navigation highlight for measure ${measureNumber}`);
          }
        }
      }
    }
  }
  
  clearNavigationHighlight() {
    const svg = this.container.querySelector('svg');
    if (!svg) return;
    
    const highlights = svg.querySelectorAll('[data-navigation-highlight="true"]');
    highlights.forEach(h => h.remove());
  }

  findAndPlayNoteAtCoordinates(osmdX, osmdY, svgX, svgY) {
    if (!this.osmd?.graphic?.measureList || !this.onPlayNote) {
      return;
    }

    let closestNote = null;
    let closestDistance = Infinity;
    const tolerance = 2; // OSMD units - precise clicking to match cursor detection

    // Search through all measures for notes
    for (let measureIndex = 0; measureIndex < this.osmd.graphic.measureList.length; measureIndex++) {
      const measureRow = this.osmd.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const graphicalMeasure = measureRow[staffIndex];
        
        if (graphicalMeasure && this.isMeasureVisible(graphicalMeasure)) {
          // Search through all staff entries (note containers) in this measure
          for (let entryIndex = 0; entryIndex < graphicalMeasure.staffEntries.length; entryIndex++) {
            const staffEntry = graphicalMeasure.staffEntries[entryIndex];
            
            if (staffEntry && staffEntry.graphicalVoiceEntries) {
              // Search through voice entries
              for (let voiceIndex = 0; voiceIndex < staffEntry.graphicalVoiceEntries.length; voiceIndex++) {
                const voiceEntry = staffEntry.graphicalVoiceEntries[voiceIndex];
                
                if (voiceEntry && voiceEntry.notes) {
                  // Search through individual notes
                  for (let noteIndex = 0; noteIndex < voiceEntry.notes.length; noteIndex++) {
                    const graphicalNote = voiceEntry.notes[noteIndex];
                    
                    if (graphicalNote && graphicalNote.PositionAndShape && graphicalNote.sourceNote && !graphicalNote.sourceNote.isRestFlag) {
                      const notePos = graphicalNote.PositionAndShape.AbsolutePosition;
                      const distance = Math.sqrt(
                        Math.pow(osmdX - notePos.x, 2) + Math.pow(osmdY - notePos.y, 2)
                      );
                      
                      if (distance < tolerance && distance < closestDistance) {
                        closestDistance = distance;
                        closestNote = {
                          graphicalNote,
                          sourceNote: graphicalNote.sourceNote,
                          staffIndex,
                          measureNumber: graphicalMeasure.MeasureNumber
                        };
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // Play the closest note if found
    if (closestNote && this.onPlayNote) {
      this.onPlayNote(closestNote.sourceNote, closestNote.staffIndex, closestNote.measureNumber);
    }
  }

}